import type { RoomId } from '@engineer-os/chat';
import { sound } from '../../lib/sound';
import { useStrings } from '../../state/useLocale';
import { Button } from '../../ui';

/**
 * "Sign in to post."
 *
 * What sits where the composer would, for someone reading a room they have
 * not joined. It is the twin of NamePrompt and takes the same slot for the
 * same reason: the conversation stays above it, so the thing being asked for
 * is visible directly over the reason to give it. A learner who can see the
 * room decides whether it is worth an account; one shown a wall decides it is
 * not.
 *
 * Only rendered where signing in is possible. A deployment with no Google
 * configured has no gate at all — the server says so by leaving the room
 * ungated, and the panel says so by showing NamePrompt instead, exactly as
 * before (worker/room.ts, `gated`).
 *
 * Why the reason is named rather than the rule. "Sign in to post" is a rule;
 * "so the room knows who it is talking to" is why the rule exists, and it is
 * also the honest one — a tag on an account is the same person on every
 * device, which is what makes a name worth anything to the people reading it.
 */
export interface SignInPromptProps {
  room: RoomId;
  busy: boolean;
  onSignIn(): void;
}

export function SignInPrompt({ room, busy, onSignIn }: SignInPromptProps) {
  const s = useStrings().chat;
  return (
    <div className="chat-signin">
      <p className="label">{s.signIn.title}</p>
      <p className="note">{s.signIn.note(room)}</p>
      <div className="chat-signin-actions">
        <Button
          variant="solid"
          disabled={busy}
          title={s.signIn.why}
          onClick={() => {
            sound.click();
            onSignIn();
          }}
        >
          {busy ? s.signIn.busy : s.signIn.button}
        </Button>
      </div>
    </div>
  );
}

export default SignInPrompt;
